<template>
    <v-container class="pa-5" fluid>
        <PrintReportsDialog
            :is-open="printReportsDialog"
            :job-id="currentJobId"
            :action-loading="actionLoading.printReports"
            @on-confirm="printReports"
            @on-abort="closePrintReportsDialog"
        />
        <page-title>Zlecenia</page-title>

        <v-row align="center">
            <v-col cols="12" sm="6">
                <v-btn
                    color="secondary"
                    medium
                    class="mx-0"
                    data-sentry-label="Utwórz szkic zlecenia"
                    :loading="createTaskLoading"
                    @click="createTask"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Utwórz szkic zlecenia
                </v-btn>
            </v-col>

            <v-col cols="12" sm="6">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col sm="auto">
                            <v-checkbox
                                v-model="filters.status_ids"
                                label="Ukryj anulowane"
                                hide-details
                                class="mt-2"
                                @change="changeFilter"
                            />
                        </v-col>
                        <v-col cols="12" sm="12" md="8" lg="6">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="jobs"
                        :loading="jobsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="jobsData"
                        @pagination="updatePagination"
                    >
                        <template #item.number="{item}">
                            {{ item.number }}
                        </template>
                        <template #item.name="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.customer="{item}">
                            <template v-if="item.customer">
                                {{ item.customer.name }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>

                        <template #item.status="{item}">
                            {{ item.status.name }}
                        </template>
                        <template #item.createdAt="{item}">
                            {{ formatDate(item.createdAt) }}
                        </template>
                        <template #item.createdBy="{item}">
                            <template v-if="item.createdBy && item.createdBy.firstName && item.createdBy.lastName">
                                {{ item.createdBy.firstName }} {{ item.createdBy.lastName }}
                            </template>
                            <template v-else>
                                -
                            </template>
                        </template>
                        <template #item.processStatus="{item}">
                            {{ item.processStatus }}
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip v-if="item.status.id === 40 || item.status.id === 20" :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Generuj raport"
                                            v-on="on"
                                            @click="showPrintReportsDialog(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-printer
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Generuj raport</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="openJob(item.id)"
                                        >
                                            <v-icon color="teal">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import router from '@/router'
import PageTitle from '@/components/common/PageTitle'
import PrintReportsDialog from '@/views/Jobs/Common/PrintReportsDialog'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { openPdfFile } from '@/utils/pdfHandler'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'Jobs',
    components: {
        PageTitle, PrintReportsDialog
    },
    mixins: [
        filters,
        pagination,
        dateFormatter,
        tooltipDelay,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('jobs/clearJobsFilters')
            this.clearSelectedListItemId()
        }
        next()
    },
    data() {
        return {
            currentJobId: null,
            createTaskLoading: false,
            actionLoading: {
                printReports: false
            },
            printReportsDialog: false,
            headers: [
                {
                    text: 'Numer zlecenia',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Nazwa zlecenia',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Klient',
                    value: 'customer',
                    sortable: false
                },
                {
                    text: 'Status',
                    value: 'status',
                    sortable: false
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdAt',
                    sortable: false
                },
                {
                    text: 'Utworzone przez',
                    value: 'createdBy',
                    sortable: false
                },
                {
                    text: 'Etap',
                    value: 'processStatus',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        jobs() {
            return this.$store.state.jobs.jobs.jobs
        },
        jobsLoading() {
            return this.$store.state.jobs.jobs.jobsLoading
        },
        selectedJobId() {
            return this.$store.state.common.selectedListItemId
        },
        savedFilters() {
            return this.$store.state.jobs.jobs.filters
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    if (item === 'status_ids') {
                        filters[item] = '10,20,30,40'
                    }
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getJobs()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedJobId) {
                return 'isSelected'
            }
        },
        getJobs() {
            this.$store.dispatch('jobs/getJobs', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openJob(jobId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(jobId)
            router.push({
                name: 'jobsJob',
                params: {
                    jobId: jobId
                }
            })
                .catch(error => errorHandler(error))
        },
        showPrintReportsDialog(jobId) {
            this.currentJobId = jobId
            this.printReportsDialog = true
        },
        closePrintReportsDialog() {
            this.currentJobId = null
            this.printReportsDialog = false
        },
        printReports(data) {
            this.actionLoading.printReports = true
            this.$store.dispatch('jobs/printJobReports', data)
                .then(response => {
                    openPdfFile(response.data)
                    this.closePrintReportsDialog()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.printReports = false
                })
        },
        createTask() {
            this.createTaskLoading = true
            router.push({ name: 'orderComponentsList' })
                .catch(error => {
                    this.createTaskLoading = false
                    errorHandler(error)
                })
        },
        changeFilter() {
            this.$store.dispatch('jobs/setJobsFilters', this.filters)
            this.resetPagination()
            this.getJobs()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getJobs()
            }
            this.clearPaginationPending()
        },
    }
}
</script>
